import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text, Button } from '@lnr-v2/ui';
import type { NewLeadDetail, MetricTrend } from '@lnr-v2/types';
import { Modal } from '../../atoms/Modal';
import styles from './NewLeadsModal.module.css';

export interface NewLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalLeads: number;
  trend?: MetricTrend;
  leads: NewLeadDetail[];
  onConvertLead?: (leadId: string) => Promise<void>;
  loading?: boolean;
}

export const NewLeadsModal: React.FC<NewLeadsModalProps> = ({
  isOpen,
  onClose,
  totalLeads,
  trend,
  leads,
  onConvertLead,
  loading = false,
}) => {
  const navigate = useNavigate();

  const handleViewAllProjects = () => {
    navigate('/admin/cases');
    onClose();
  };

  const handleConvertLead = async (leadId: string) => {
    if (onConvertLead) {
      await onConvertLead(leadId);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const renderTrendIcon = () => {
    if (!trend) return null;

    const icons = {
      up: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={styles.trendIconUp}
        >
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
      down: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={styles.trendIconDown}
        >
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
          <polyline points="17 18 23 18 23 12" />
        </svg>
      ),
      neutral: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={styles.trendIconNeutral}
        >
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      ),
    };

    return icons[trend.direction];
  };

  const renderClientAvatar = (client: NewLeadDetail['client']) => {
    if (client?.logo) {
      return (
        <img
          src={client.logo}
          alt={client.name}
          className={styles.clientLogo}
          loading="lazy"
          width="48"
          height="48"
        />
      );
    }

    // Default avatar with initials
    const initials = client?.name
      ? client.name
          .split(' ')
          .map((word) => word[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : '?';

    return <div className={styles.clientInitials}>{initials}</div>;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New Leads This Month"
      size="large"
    >
      <div className={styles.container}>
        {/* Summary Section */}
        <div className={styles.summary}>
          <div className={styles.totalLeads}>
            <Heading as="h3" className={styles.leadsNumber}>
              {totalLeads.toLocaleString()}
            </Heading>
            <Text size="sm" className={styles.leadsLabel}>
              Total Leads
            </Text>
          </div>
          {trend && (
            <div className={styles.trend}>
              <div className={styles.trendIndicator}>
                {renderTrendIcon()}
                <Text size="sm" weight="semibold" className={styles.trendValue}>
                  {trend.value}
                </Text>
              </div>
              <Text size="xs" className={styles.trendLabel}>
                {trend.label}
              </Text>
            </div>
          )}
        </div>

        {/* Leads Section */}
        <div className={styles.section}>
          <Heading as="h4" size="md" className={styles.sectionTitle}>
            Recent Leads
          </Heading>

          {leads.length === 0 ? (
            <div className={styles.emptyState}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                className={styles.emptyIcon}
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              <Text size="md" weight="semibold" className={styles.emptyTitle}>
                No new leads yet this month
              </Text>
              <Text size="sm" className={styles.emptySubtitle}>
                Check back later or create new draft projects to track leads
              </Text>
            </div>
          ) : (
            <div className={styles.leadsList}>
              {leads.map((lead) => (
                <div key={lead.id} className={styles.leadItem}>
                  <div className={styles.leadAvatar}>
                    {renderClientAvatar(lead.client)}
                  </div>
                  <div className={styles.leadContent}>
                    <div className={styles.leadInfo}>
                      <Text
                        size="sm"
                        weight="semibold"
                        className={styles.leadTitle}
                      >
                        {lead.title}
                      </Text>
                      <Text size="xs" className={styles.leadClient}>
                        {lead.client?.name || 'No client assigned'}
                      </Text>
                      <div className={styles.leadMeta}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={styles.dateIcon}
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <Text size="xs" className={styles.leadDate}>
                          {formatDate(lead.createdAt)}
                        </Text>
                      </div>
                    </div>
                    {onConvertLead && (
                      <button
                        type="button"
                        className={styles.convertButton}
                        onClick={() => handleConvertLead(lead.id)}
                        disabled={loading}
                      >
                        Convert to Project →
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleViewAllProjects}>
            View All Projects →
          </Button>
        </div>
      </div>
    </Modal>
  );
};
